// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NotesIcon from '@mui/icons-material/Notes';
import TerminalIcon from '@mui/icons-material/Terminal';
import { getSkin, resolvePalette, setAppearance, useAppearance } from '../lib/appearance';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

// A tiny mock of the chat in the scheme's palette: frame, conversation, the
// user's bubble and an accent button
const SchemePreview = ({ palette: p }) => (
  <Box sx={{ display: 'flex', height: 56, bgcolor: p.chatBg, borderRadius: 1, overflow: 'hidden' }}>
    <Box sx={{ width: 18, bgcolor: p.navBg }} />
    <Box sx={{ flex: 1, p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ alignSelf: 'flex-end', width: '55%', height: 12, borderRadius: 0.5, bgcolor: p.userBubbleBg }} />
      <Box sx={{ width: '75%', height: 5, borderRadius: 0.5, bgcolor: p.textSecondary || 'rgba(127,127,127,0.4)', opacity: 0.6 }} />
      <Box sx={{ mt: 'auto', alignSelf: 'flex-end', width: 14, height: 8, borderRadius: 0.5, bgcolor: p.accent }} />
    </Box>
  </Box>
);

// The three appearance choices. Used in the header popup and in the
// Appearance tab of the profile settings.
export const AppearancePanel = () => {
  const { mode, style, scheme } = useAppearance();
  const schemes = Object.entries(getSkin().schemes);

  return (
    <Box>
      <Section title="Mode">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(e, v) => v && setAppearance({ theme: v })}
        >
          <ToggleButton value="light"><LightModeIcon fontSize="small" sx={{ mr: 1 }} />Light</ToggleButton>
          <ToggleButton value="dark"><DarkModeIcon fontSize="small" sx={{ mr: 1 }} />Dark</ToggleButton>
        </ToggleButtonGroup>
      </Section>

      <Section title="Style">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={style}
          onChange={(e, v) => v && setAppearance({ ui_style: v })}
        >
          <ToggleButton value="normal"><NotesIcon fontSize="small" sx={{ mr: 1 }} />Normal</ToggleButton>
          <ToggleButton value="terminal"><TerminalIcon fontSize="small" sx={{ mr: 1 }} />Terminal</ToggleButton>
        </ToggleButtonGroup>
      </Section>

      <Section title="Color">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1.5 }}>
          {schemes.map(([id, def]) => {
            const selected = id === scheme;
            return (
              <Box
                key={id}
                component="button"
                type="button"
                onClick={() => setAppearance({ color_scheme: id })}
                sx={{
                  p: 0.75,
                  border: '2px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'transparent',
                  color: 'text.primary',
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                  '&:hover': { borderColor: selected ? 'primary.main' : 'text.secondary' },
                }}
              >
                <SchemePreview palette={resolvePalette(id, mode)} />
                <Typography variant="body2" sx={{ mt: 0.75, fontWeight: selected ? 600 : 400 }}>
                  {def.label || id}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Section>
    </Box>
  );
};

export const AppearanceDialog = ({ open, onClose }) => (
  // Invisible backdrop: the page must stay undimmed to judge the live preview
  // (a click outside still closes the popup)
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ backdrop: { invisible: true } }}>
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      Appearance
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <AppearancePanel />
    </DialogContent>
  </Dialog>
);
